import { AlgorithmResult, StatisticalMetrics } from '../types';

function calculatePercentile(sortedValues: number[], percentile: number): number {
  if (sortedValues.length === 0) {
    return 0;
  }

  if (sortedValues.length === 1) {
    return sortedValues[0];
  }

  const boundedPercentile = Math.max(0, Math.min(1, percentile));
  const index = (sortedValues.length - 1) * boundedPercentile;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  const fraction = index - lowerIndex;

  if (lowerIndex === upperIndex) {
    return sortedValues[lowerIndex];
  }

  return sortedValues[lowerIndex] + (sortedValues[upperIndex] - sortedValues[lowerIndex]) * fraction;
}

/**
 * Calculates statistical metrics (mean, std dev, min, max, median) for a set of values
 */
export function calculateStatistics(values: number[]): StatisticalMetrics {
  if (values.length === 0) {
    return {
      mean: 0,
      stdDev: 0,
      min: 0,
      max: 0,
      median: 0,
      q1: 0,
      q3: 0,
    };
  }

  // Sort values for median calculation
  const sorted = [...values].sort((a, b) => a - b);
  
  // Calculate mean
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  
  // Calculate standard deviation
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  
  // Min, max, median
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const median = sorted.length % 2 === 0
    ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
    : sorted[Math.floor(sorted.length / 2)];
  const q1 = calculatePercentile(sorted, 0.25);
  const q3 = calculatePercentile(sorted, 0.75);

  return {
    mean,
    stdDev,
    min,
    max,
    median,
    q1,
    q3,
  };
}

/**
 * Calculates statistics for an algorithm across multiple runs
 */
export function calculateAlgorithmStatistics(
  results: AlgorithmResult[]
): {
  fitness: StatisticalMetrics;
  energy: StatisticalMetrics;
  cost: StatisticalMetrics;
  responseTime: StatisticalMetrics;
  executionTime: StatisticalMetrics;
} {
  if (results.length === 0) {
    const emptyStats: StatisticalMetrics = {
      mean: 0,
      stdDev: 0,
      min: 0,
      max: 0,
      median: 0,
      q1: 0,
      q3: 0,
    };
    return {
      fitness: emptyStats,
      energy: emptyStats,
      cost: emptyStats,
      responseTime: emptyStats,
      executionTime: emptyStats,
    };
  }

  // CRITICAL: Filter out non-finite values for ALL metrics before calculating statistics
  // This prevents corrupting statistics (mean, min, max, std dev, etc.) with invalid data
  // Even required metrics (fitness, energy, cost, responseTime) should be filtered defensively
  // Optional metrics (executionTime) must be filtered to avoid converting missing values to 0
  const fitnessValues = results
    .map(r => r.fitness)
    .filter((v): v is number => Number.isFinite(v));
  const energyValues = results
    .map(r => r.energy)
    .filter((v): v is number => Number.isFinite(v));
  const costValues = results
    .map(r => r.cost)
    .filter((v): v is number => Number.isFinite(v));
  const responseTimeValues = results
    .map(r => r.responseTime)
    .filter((v): v is number => Number.isFinite(v));
  // CRITICAL: Use ?? NaN to explicitly mark missing executionTime as NaN
  // This prevents treating missing values as 0, which would corrupt mean/min statistics
  // (missing executionTime should not appear as "best possible" 0)
  const executionTimeValues = results
    .map(r => r.executionTime ?? NaN)
    .filter((v): v is number => Number.isFinite(v));

  return {
    fitness: calculateStatistics(fitnessValues),
    energy: calculateStatistics(energyValues),
    cost: calculateStatistics(costValues),
    responseTime: calculateStatistics(responseTimeValues),
    executionTime: calculateStatistics(executionTimeValues), // Returns zeros if empty (handled by calculateStatistics)
  };
}
